import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  List,
  message,
  Modal,
  Select,
  Switch,
  Tag,
  Typography,
} from 'antd';
import { EditOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons';

import api from '../../services/api';
import { CadastroPage } from './CadastroPage';
import {
  listarClientesV2,
  listarGruposV2,
  listarUsuariosV2,
} from '../../services/cadastrosV2';

const onlyNumbers = value => (value || '').replace(/\D/g, '');
const formatCpf = value =>
  onlyNumbers(value).replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
const normalizeClient = client => {
  const detail = client?.cliente || client;
  const id = client?.idCliente || detail?.id;

  return {
    id,
    nomeFantasia:
      detail?.nomeFantasia || detail?.razaoSocial || `Cliente #${id}`,
  };
};

export function UsuariosV2() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [originalGroups, setOriginalGroups] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [clientPickerOpen, setClientPickerOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientPickerLoading, setClientPickerLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarUsuariosV2(
        page,
        debouncedSearch,
        onlyActive ? true : undefined
      );
      setItems(response?.data?.items || []);
      setTotal(response?.data?.count || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      message.error(
        error?.response?.data?.detail ||
          'Não foi possível carregar os usuários.'
      );
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, onlyActive]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const loadOptions = async () => {
    try {
      const groupResponse = await listarGruposV2('');
      setGroups(groupResponse?.data || []);
    } catch (error) {
      message.error('Não foi possível carregar os grupos.');
    }
  };

  const openClientPicker = () => {
    setClientSearch('');
    setClientResults([]);
    setClientPickerOpen(true);
  };

  const searchClients = async value => {
    const name = value.trim();
    if (!name) {
      message.warning('Informe o nome do cliente para buscar.');
      return;
    }

    setClientPickerLoading(true);
    try {
      const response = await listarClientesV2(1, name, 10, true);
      setClientResults(response?.data?.items || []);
    } catch (error) {
      setClientResults([]);
      message.error('Não foi possível buscar os clientes.');
    } finally {
      setClientPickerLoading(false);
    }
  };

  const toggleClient = client => {
    const normalized = normalizeClient(client);
    setSelectedClients(current =>
      current.some(item => item.id === normalized.id)
        ? current.filter(item => item.id !== normalized.id)
        : [...current, normalized]
    );
  };

  const openNew = async () => {
    setEditing(null);
    setOriginalGroups([]);
    setSelectedClients([]);
    form.resetFields();
    form.setFieldsValue({ ativo: true, grupos: [] });
    setOpen(true);
    await loadOptions();
  };

  const openEdit = async item => {
    setEditing(item);
    setOpen(true);
    setSaving(true);
    try {
      const linkedClientsPromise = Promise.all(
        (item.clientes || []).map(async client => {
          if (client?.cliente?.nomeFantasia || client?.nomeFantasia) {
            return normalizeClient(client);
          }

          const id = client?.idCliente || client?.id;
          try {
            const response = await api.get('/Cliente', { params: { id } });
            return normalizeClient(response?.data || client);
          } catch (error) {
            return normalizeClient(client);
          }
        })
      );
      const [, groupResponse, linkedClients] = await Promise.all([
        loadOptions(),
        api.get(`/Usuario/${item.id}/grupos`),
        linkedClientsPromise,
      ]);
      const groupIds = (groupResponse?.data || []).map(group => group.id);
      setOriginalGroups(groupIds);
      setSelectedClients(linkedClients.filter(client => client.id));
      form.setFieldsValue({
        nome: item.nome,
        cpf: formatCpf(item.cpf || item.login),
        email: item.email,
        ativo: item.ativo,
        grupos: groupIds,
      });
    } catch (error) {
      message.error('Não foi possível carregar os dados do usuário.');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    setOriginalGroups([]);
    setSelectedClients([]);
    setClientPickerOpen(false);
    setClientSearch('');
    setClientResults([]);
    form.resetFields();
  };

  const save = async values => {
    if (selectedClients.length === 0) {
      message.error('Selecione ao menos um cliente.');
      return;
    }

    setSaving(true);
    const selectedGroups = values.grupos || [];
    const payload = {
      id: editing?.id || 0,
      cpf: onlyNumbers(values.cpf),
      nome: values.nome.trim(),
      email: values.email.trim(),
      senha: values.senha || undefined,
      ativo: values.ativo,
      idClientes: selectedClients.map(client => client.id),
    };

    try {
      const response = editing
        ? await api.put('/Usuario', payload)
        : await api.post('/Usuario', payload);
      const userId = editing?.id || response?.data?.id;
      const toAdd = selectedGroups.filter(id => !originalGroups.includes(id));
      const toRemove = originalGroups.filter(
        id => !selectedGroups.includes(id)
      );

      await Promise.all([
        ...toAdd.map(id => api.put(`/Usuario/${userId}/groups/${id}`)),
        ...toRemove.map(id => api.delete(`/Usuario/${userId}/groups/${id}`)),
      ]);

      message.success(
        `Usuário ${editing ? 'atualizado' : 'cadastrado'} com sucesso.`
      );
      close();
      await load();
    } catch (error) {
      message.error(
        error?.response?.data?.detail || 'Não foi possível salvar o usuário.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CadastroPage
        title="Usuários"
        newLabel="Novo usuário"
        search={search}
        searchPlaceholder="Buscar usuários por nome, e-mail ou CPF"
        onSearch={setSearch}
        loading={loading}
        items={items}
        total={total}
        page={page}
        onPageChange={setPage}
        onNew={openNew}
        extraFilters={
          <Checkbox
            checked={onlyActive}
            onChange={event => {
              setOnlyActive(event.target.checked);
              setPage(1);
            }}
          >
            Somente ativos
          </Checkbox>
        }
        renderItem={item => (
          <List.Item
            key={item.id}
            actions={[
              <Button
                key="edit"
                type="link"
                icon={<EditOutlined />}
                onClick={() => openEdit(item)}
              >
                Editar
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={item.nome}
              description={`${formatCpf(
                item.cpf || item.login
              )} • ${item.email || 'Sem e-mail'}`}
            />
            <Tag color={item.ativo ? 'green' : 'default'}>
              {item.ativo ? 'Ativo' : 'Inativo'}
            </Tag>
          </List.Item>
        )}
      />

      <Modal
        open={open}
        title={editing ? 'Editar usuário' : 'Novo usuário'}
        footer={null}
        width={680}
        maskClosable={false}
        destroyOnHidden
        onCancel={close}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={save}
          initialValues={{ ativo: true }}
        >
          <Form.Item
            name="nome"
            label="Nome"
            rules={[{ required: true, message: 'Informe o nome.' }]}
          >
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item
            name="cpf"
            label="CPF"
            rules={[{ required: true, message: 'Informe o CPF.' }]}
          >
            <Input maxLength={14} />
          </Form.Item>
          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              {
                required: true,
                type: 'email',
                message: 'Informe um e-mail válido.',
              },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="senha"
            label={editing ? 'Nova senha (opcional)' : 'Senha'}
            rules={[{ required: !editing, message: 'Informe a senha.' }]}
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="grupos"
            label="Grupos"
            rules={[
              { required: true, message: 'Selecione ao menos um grupo.' },
            ]}
          >
            <Select
              mode="multiple"
              showSearch
              optionFilterProp="label"
              options={groups.map(group => ({
                label: group.nome,
                value: group.id,
              }))}
            />
          </Form.Item>
          <Form.Item label="Clientes" required>
            <div className="cadastros-v2-client-field">
              <div className="cadastros-v2-client-selection">
                {selectedClients.length > 0 ? (
                  selectedClients.map(client => (
                    <Tag
                      key={client.id}
                      closable
                      onClose={() =>
                        setSelectedClients(current =>
                          current.filter(item => item.id !== client.id)
                        )
                      }
                    >
                      {client.nomeFantasia}
                    </Tag>
                  ))
                ) : (
                  <Typography.Text type="secondary">
                    Nenhum cliente selecionado
                  </Typography.Text>
                )}
              </div>
              <Button
                icon={<PlusOutlined />}
                aria-label="Selecionar cliente"
                title="Selecionar cliente"
                onClick={openClientPicker}
              />
            </div>
          </Form.Item>
          <Form.Item name="ativo" label="Status" valuePropName="checked">
            <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
          </Form.Item>
          <div className="cadastros-v2-modal-actions">
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Salvar
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        open={clientPickerOpen}
        title="Selecionar clientes ativos"
        okText="Concluir"
        cancelText="Cancelar"
        onOk={() => setClientPickerOpen(false)}
        onCancel={() => setClientPickerOpen(false)}
        destroyOnHidden
      >
        <Input.Search
          allowClear
          autoFocus
          enterButton={<SearchOutlined />}
          value={clientSearch}
          onChange={event => setClientSearch(event.target.value)}
          onSearch={searchClients}
          placeholder="Informe o nome do cliente"
          aria-label="Buscar cliente por nome"
          loading={clientPickerLoading}
        />
        <List
          className="cadastros-v2-client-results"
          loading={clientPickerLoading}
          dataSource={clientResults}
          locale={{ emptyText: 'Informe um nome e clique em buscar' }}
          renderItem={client => {
            const checked = selectedClients.some(item => item.id === client.id);
            return (
              <List.Item key={client.id}>
                <Checkbox
                  checked={checked}
                  onChange={() => toggleClient(client)}
                >
                  {client.nomeFantasia}
                </Checkbox>
              </List.Item>
            );
          }}
        />
      </Modal>
    </>
  );
}
